import { useContext, useEffect } from 'react';
import type { TSectionId } from '~/constants/vdp/section';
import { SectionReadinessActionsContext } from '~/context/vdp/SectionReadinessContext';

/**
 * Registers a VDP section with the SectionReadinessContext and signals when it
 * has finished rendering its layout-affecting content.
 *
 * Call this in the section component that is the parent of its `<SectionContainer>`,
 * NOT inside `SectionContainer` itself — the hook must run even when the component
 * returns `null` so that late-mounting sections are visible to the scroll gate.
 *
 * On first mount the section is registered. When `isReady` becomes `true` the
 * section is marked ready, signaling that it will not cause further layout shift.
 * Sections without async data can omit `isReady` (defaults to `true`) to register
 * and mark ready in a single pass. Sections that depend on async data should pass
 * their loaded state so the scroll gate waits for content to settle before scrolling.
 *
 * @param sectionId - The section identifier from `SectionId`.
 * @param isReady - Whether the section has finished loading layout-affecting content.
 */
export default function useMarkSectionReady(
  sectionId: TSectionId,
  isReady = true
) {
  const { markSectionReady, registerSection } = useContext(
    SectionReadinessActionsContext
  );

  useEffect(() => {
    registerSection(sectionId);
  }, [registerSection, sectionId]);

  useEffect(() => {
    if (isReady) {
      markSectionReady(sectionId);
    }
  }, [isReady, markSectionReady, sectionId]);
}
